import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { searchAll, searchIndex } from '../lib/searchIndex.js';
import { ArrowRight, X } from './Icons.jsx';

export default function Search({ open, onClose }) {
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const results = q.trim() === ''
    ? searchIndex.slice(0, 10)
    : searchAll(q).slice(0, 20);

  useEffect(() => {
    if (open) {
      setQ(''); setActive(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(results.length - 1, a + 1)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
      else if (e.key === 'Enter' && results[active]) { e.preventDefault(); navigate(results[active].path); onClose(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, results, active, navigate, onClose]);

  useEffect(() => { setActive(0); }, [q]);

  if (!open) return null;

  return (
    <div style={backdrop} onClick={onClose}>
      <div style={panel} onClick={(e) => e.stopPropagation()}>
        <div style={inputWrap}>
          <span style={{ fontSize: 18, color: 'var(--text-muted)' }}>⌕</span>
          <input
            ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search pages, drills, tools…"
            style={input}
          />
          <kbd style={kbd}>esc</kbd>
        </div>

        <div style={listWrap}>
          {results.length === 0 ? (
            <div style={empty}>Nothing matches "{q}".</div>
          ) : (
            results.map((r, i) => (
              <Link
                key={r.path + r.title}
                to={r.path}
                onClick={onClose}
                onMouseEnter={() => setActive(i)}
                style={{
                  ...row,
                  background: active === i ? 'var(--accent-soft)' : '#fff',
                  borderLeft: active === i ? '3px solid var(--brand-purple)' : '3px solid transparent'
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                    <strong style={{ fontSize: 14, color: 'var(--text)' }}>{r.title}</strong>
                    <span style={categoryChip}>{r.category}</span>
                  </div>
                  <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.desc}</div>
                </div>
                <ArrowRight width="14" height="14" style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
              </Link>
            ))
          )}
        </div>

        <div style={footer}>
          <div style={{ display: 'flex', gap: 14, fontSize: 11, color: 'var(--text-muted)' }}>
            <span><kbd style={kbdInline}>↑</kbd><kbd style={kbdInline}>↓</kbd> navigate</span>
            <span><kbd style={kbdInline}>↵</kbd> open</span>
            <span><kbd style={kbdInline}>esc</kbd> close</span>
          </div>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{results.length} of {searchIndex.length}</span>
        </div>
      </div>
    </div>
  );
}

const backdrop = { position: 'fixed', inset: 0, background: 'rgba(35, 20, 63, 0.55)', backdropFilter: 'blur(6px)', display: 'grid', placeItems: 'start center', zIndex: 300, padding: '12vh 16px 16px' };
const panel = { width: '100%', maxWidth: 600, background: '#fff', borderRadius: 16, boxShadow: '0 40px 100px rgba(35, 20, 63, 0.4)', overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: '70vh' };
const inputWrap = { display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', borderBottom: '1px solid var(--border)' };
const input = { flex: 1, border: 'none', outline: 'none', fontSize: 16, background: 'transparent', fontFamily: 'inherit' };
const kbd = { padding: '2px 6px', borderRadius: 4, background: 'var(--card-soft)', border: '1px solid var(--border)', fontSize: 11, fontFamily: 'monospace', color: 'var(--text-muted)' };
const kbdInline = { padding: '1px 5px', borderRadius: 3, background: 'var(--card-soft)', border: '1px solid var(--border)', fontSize: 11, fontFamily: 'monospace', marginRight: 4 };
const listWrap = { overflowY: 'auto', flex: 1 };
const row = { display: 'flex', alignItems: 'center', gap: 12, padding: '11px 16px', textDecoration: 'none', color: 'inherit', borderBottom: '1px solid var(--border)' };
const categoryChip = { fontSize: 10.5, fontWeight: 700, color: 'var(--brand-purple)', textTransform: 'uppercase', letterSpacing: '0.08em' };
const empty = { padding: '28px 18px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 };
const footer = { padding: '10px 16px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--card-soft)' };
